import { AxiosError } from "axios";

import { ADMIN_API_ENDPOINTS } from "@/config/apiEndpoints/adminUrls";
import { handleApiError } from "@/lib/utils";
import adminAxios from "@/lib/axios";

export interface IEligibilityCategory {
  id?: number;
  eligibility_id?: number;
  name?: string;
  content?: string;
  order_index?: number;
  is_active?: boolean;
  status?: boolean;
}

export interface IEligibility {
  id?: number | string;
  page_id?: number;
  component_id?: number;
  title?: string;
  title_text_color?: string;
  description?: string;
  description_text_color?: string;
  background_color?: string;
  background_gradient_start_color?: string;
  background_gradient_end_color?: string;
  background_gradient_angle?: string;
  category_border_color?: string;
  category_active_text_color?: string;
  category_active_background_color?: string;
  category_inactive_text_color?: string;
  content_text_color?: string;
  button_text?: string;
  button_text_color?: string;
  button_color?: string;
  button_icon?: string;
  button_icon_alt?: string;
  read_more_text?: string;
  read_more_color?: string;
  read_more_url?: string;
  variant?: string;
  metadata?: any[];
  categories?: IEligibilityCategory[];
  status?: boolean;
}

export async function createEligibility(payload: Partial<IEligibility>) {
  try {
    const response = await adminAxios.post(
      ADMIN_API_ENDPOINTS.ELIGIBILITY.CREATE,
      payload
    );

    const status = response?.status ?? 0;
    if (status >= 200 && status < 300) {
      return response?.data ?? null;
    }
    return null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function updateEligibility(id: number, payload: Partial<IEligibility>) {
  try {
    const response = await adminAxios.put(
      ADMIN_API_ENDPOINTS.ELIGIBILITY.UPDATE(id),
      payload
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function getEligibilityByComponentId(componentId: number) {
  try {
    const response = await adminAxios.get(
      ADMIN_API_ENDPOINTS.ELIGIBILITY.GET_BY_COMPONENT_ID(componentId)
    );
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function getEligibilityCategoriesByComponentId(componentId: number) {
  try {
    const response = await adminAxios.get(
      ADMIN_API_ENDPOINTS.ELIGIBILITY.CATEGORY.GET_BY_COMPONENT_ID(componentId)
    );
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function createEligibilityCategory(
  payload: Partial<IEligibilityCategory> & { component_id?: number }
) {
  try {
    const response = await adminAxios.post(
      ADMIN_API_ENDPOINTS.ELIGIBILITY.CATEGORY.CREATE,
      payload
    );
    return response?.status === 201 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function updateEligibilityCategory(
  id: number,
  payload: Partial<IEligibilityCategory>
) {
  try {
    const response = await adminAxios.put(
      ADMIN_API_ENDPOINTS.ELIGIBILITY.CATEGORY.UPDATE(id),
      payload
    );
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function deleteEligibilityCategory(id: number) {
  try {
    const response = await adminAxios.delete(
      ADMIN_API_ENDPOINTS.ELIGIBILITY.CATEGORY.DELETE(id)
    );
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function cloneEligibilityWithCategories(
  sourceEligibilityId: number,
  targetPageComponentId: number,
  targetEligibilityId?: number | null
) {
  try {
    const response = await adminAxios.post(ADMIN_API_ENDPOINTS.ELIGIBILITY.CLONE, {
      source_eligibility_id: sourceEligibilityId,
      target_page_component_id: targetPageComponentId,
      target_eligibility_id: targetEligibilityId ?? null,
    });

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

